<template>
  <div class="pc-text-input-wrapper" :class="{disabled}">
    <input-label v-if="label" :for="$refs.input" :disabled="disabled">
      {{ fieldLabel }}
    </input-label>
    <div class="pc-text-input">
      <input
        ref="input"
        v-click-outside="emitFocusOut"
        :value="UIValue"
        :disabled="disabled"
        :type="getInputType"
        :placeholder="placeholder"
        class="input"
        :class="{'is-placeholder': !value}"
        @input="inputEmited"
        @focusin="emitFocusIn"
        @focusout="emitFocusOut"
      />
      <icu-icon
        v-if="isPassword"
        class="pc-text-input__password-icon"
        :icon-name="hidePassword ? 'pc-visible' : 'pc-invisible'"
        @click="hidePassword = !hidePassword"
      />
      <icu-icon
        v-if="iconName"
        class="pc-text-input__password-icon"
        :icon-name="iconName"
        @click="emitFocusIn"
      />
    </div>
    <div v-if="!hideErrors" class="pc-text-input__hint-wrap">
      <input-error>
        <template v-if="errorMessage">
          {{ $t(errorMessage, errorValues) }}
        </template>
      </input-error>
      <template v-if="showHint ">
        <slot name="hint">
          <input-hint v-if="hint && (!value || forceHintOnFocus)" v-html="hint">
          </input-hint>
        </slot>
      </template>
    </div>
  </div>
</template>

<script>
import IcuIcon from '@/components/common/IcuIcon.vue'
import InputError from '@/components/inputs/InputError.vue'
import InputLabel from '@/components/inputs/InputLabel.vue'
import InputHint from '@/components/inputs/InputHint.vue'
import IMask from 'imask'
import { debounce } from 'lodash'

export default {
  name: 'PcTextInput',
  components: { InputHint, InputLabel, InputError, IcuIcon },
  availableTypes: {
    password: 'password',
    text: 'text'
  },
  props: {
    placeholder: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: null
    },
    type: {
      type: String,
      default: 'text'
    },
    label: {
      type: String,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: null
    },
    forceHint: {
      type: Boolean,
      default: false
    },
    forceHintOnFocus: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Array,
      default: () => []
    },
    hideErrors: {
      type: Boolean,
      default: false
    },
    hideHints: {
      type: Boolean,
      default: false
    },
    mask: {
      type: [String, Array, RegExp, Object],
      default: () => new RegExp(/.*/)
    },
    maskOptions: {
      type: Object,
      default: () => {}
    },
    valid:{
      type: Boolean,
      default: true
    },
    emitInputAsEvent: {
      type: Boolean,
      default: false
    },
    iconName: {
      type: String,
      default: ''
    },
    debounceTime: {
      type: Number,
      default: 300
    }
  },
  data() {
    return {
      hidePassword: true,
      isFieldInFocus: false,
      inputMask: null
    }
  },
  computed: {
    showHint(){
      return this.forceHint || (!this.errorMessage && this.isFieldInFocus)
    },
    UIValue() {
      return this.inputMask?.value || ''
    },
    isPassword() {
      return this.type === this.$options.availableTypes.password
    },
    getInputType() {
      if (this.isPassword && !this.hidePassword) {
        return this.$options.availableTypes.text
      }

      return this.type
    },
    fieldLabel() {
      if (this.required) {
        return `${this.label} *`
      }

      return this.label
    },
    errorMessage() {
      if(typeof this.errors?.[0] === 'object'){
        return this.errors?.[0].str
      }
      return this.errors?.[0] || ''
    },
    errorValues(){
      if(typeof this.errors?.[0] === 'object'){
        return this.errors?.[0].values
      }
      return null
    },
    inputEmited() {
      return debounce(this.emitInput, this.debounceTime, { leading: true })
    }
  },
  watch: {
    value: {
      handler() {
        this.updateIMaskValue()
      }
    }
  },
  mounted() {
    this.inputMask = IMask(this.$refs.input, {
      mask: this.mask,
      lazy: false,
      ...this.maskOptions
    })

    this.updateIMaskValue()
  },
  beforeDestroy() {
    this.inputMask.destroy()
  },
  methods: {
    updateIMaskValue() {
      this.inputMask.value = this.value !== null ? this.value : ''
    },
    focus() {
      this.$refs.input.focus()
    },
    emitInput(e) {
      if(this.emitInputAsEvent){
        this.$emit('input', e)
      } else {
        this.inputMask.value = e.target.value
        this.$emit('input', this.inputMask.unmaskedValue)
      }
    },
    emitFocusIn(e){
      if(!e) {
        setTimeout(() => {
          this.$refs.input.focus()
        },0)

        return
      }
      this.isFieldInFocus = true
      this.$emit('focusin', e)
    },
    emitFocusOut(e){
      this.isFieldInFocus = false
      this.$emit('focusout', e)
    }
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/components/inputs/PcTextInput.scss";
.pc-date__input {
  input {
    margin-bottom: 0;
  }
}
</style>
